import type { DocsFrontmatter } from "./types";

export function stripFrontmatter(md: string): {
	meta: DocsFrontmatter;
	body: string;
} {
	if (!md.startsWith("---")) return { meta: {}, body: md };
	const end = md.indexOf("\n---", 3);
	if (end === -1) return { meta: {}, body: md };
	const raw = md.slice(3, end).trim();
	const body = md.slice(end + 4).replace(/^\n/, "");
	const meta: DocsFrontmatter = {};
	const sources: string[] = [];
	const live: string[] = [];
	let listKey: "sources" | "live" | null = null;

	for (const line of raw.split("\n")) {
		const listItem = line.match(/^\s+-\s+(.+)$/);
		if (listItem && listKey) {
			if (listKey === "sources") sources.push(listItem[1]);
			else live.push(listItem[1]);
			continue;
		}
		const m = line.match(/^(\w+):\s*(.*)$/);
		if (!m) continue;
		const key = m[1];
		const val = m[2].trim();
		if (key === "sources") {
			listKey = "sources";
			continue;
		}
		if (key === "live") {
			listKey = "live";
			continue;
		}
		listKey = null;
		if (key === "title") meta.title = val;
		else if (key === "description") meta.description = val;
		else if (key === "section") meta.section = val;
		else if (key === "order") meta.order = val;
		else if (key === "updated") meta.updated = val;
	}
	if (sources.length) meta.sources = sources;
	if (live.length) meta.live = live;
	return { meta, body };
}

export function docsArticlePath(sectionId: string, slug: string): string {
	return `/docs/${sectionId}/${slug}`;
}

/**
 * Allow only relative / hash / http(s) links. Reject javascript:, data:, etc.
 * Returns null when the href must not become an <a href>.
 */
export function sanitizeHref(raw: string): string | null {
	const href = raw.trim();
	if (!href) return null;
	const lower = href.toLowerCase();
	if (
		lower.startsWith("javascript:") ||
		lower.startsWith("data:") ||
		lower.startsWith("vbscript:") ||
		lower.startsWith("file:")
	) {
		return null;
	}
	// Absolute http(s)
	if (lower.startsWith("https://") || lower.startsWith("http://")) {
		return href.replace(/"/g, "%22");
	}
	// Protocol-relative blocked (//evil.com)
	if (href.startsWith("//")) return null;
	// Relative, root-relative, hash, or query-only
	if (
		href.startsWith("/") ||
		href.startsWith("./") ||
		href.startsWith("../") ||
		href.startsWith("#") ||
		href.startsWith("?") ||
		(!href.includes(":") && !href.includes("\\"))
	) {
		return href.replace(/"/g, "%22");
	}
	// Unknown scheme (mailto: optional — allow mailto for docs)
	if (lower.startsWith("mailto:")) {
		return href.replace(/"/g, "%22");
	}
	return null;
}

function escapeAttr(s: string): string {
	return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

/** Very small markdown → HTML (headings, lists, code, bold, links). */
export function simpleMarkdownToHtml(md: string): string {
	const lines = md.split("\n");
	const out: string[] = [];
	let inCode = false;
	let inUl = false;
	let inOl = false;

	const flushLists = () => {
		if (inUl) {
			out.push("</ul>");
			inUl = false;
		}
		if (inOl) {
			out.push("</ol>");
			inOl = false;
		}
	};

	const inline = (s: string) =>
		s
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/`([^`]+)`/g, "<code>$1</code>")
			.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
			.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label: string, url: string) => {
				const safe = sanitizeHref(url);
				if (!safe) {
					// Escaped plain text — no executable href
					return `[${label}](${url.replace(/</g, "&lt;")})`;
				}
				const external =
					safe.startsWith("http://") || safe.startsWith("https://");
				const rel = external ? ' rel="noopener noreferrer"' : "";
				return `<a href="${escapeAttr(safe)}" class="docs-link"${rel}>${label}</a>`;
			});

	for (const line of lines) {
		if (line.startsWith("```")) {
			flushLists();
			if (!inCode) {
				inCode = true;
				out.push("<pre class=\"docs-code\"><code>");
			} else {
				inCode = false;
				out.push("</code></pre>");
			}
			continue;
		}
		if (inCode) {
			out.push(`${line.replace(/&/g, "&amp;").replace(/</g, "&lt;")}\n`);
			continue;
		}
		const h = line.match(/^(#{1,4})\s+(.+)$/);
		if (h) {
			flushLists();
			const level = h[1].length;
			out.push(`<h${level} class="docs-h">${inline(h[2])}</h${level}>`);
			continue;
		}
		const ul = line.match(/^[-*]\s+(.+)$/);
		if (ul) {
			if (inOl) {
				out.push("</ol>");
				inOl = false;
			}
			if (!inUl) {
				out.push("<ul class=\"docs-ul\">");
				inUl = true;
			}
			out.push(`<li>${inline(ul[1])}</li>`);
			continue;
		}
		const ol = line.match(/^\d+\.\s+(.+)$/);
		if (ol) {
			if (inUl) {
				out.push("</ul>");
				inUl = false;
			}
			if (!inOl) {
				out.push("<ol class=\"docs-ol\">");
				inOl = true;
			}
			out.push(`<li>${inline(ol[1])}</li>`);
			continue;
		}
		if (!line.trim()) {
			flushLists();
			continue;
		}
		flushLists();
		out.push(`<p class="docs-p">${inline(line)}</p>`);
	}
	flushLists();
	if (inCode) out.push("</code></pre>");
	return out.join("\n");
}
